"use client";

import { memo } from "react";
import { useScenePause } from "./useScenePause";

/**
 * Animated SVG illustration for the /listings card on the landing page.
 * A short "results feed", shaped like the real product: three listing
 * CARDS side by side (photo on top + price/location bars + an AI verdict
 * chip), matching the ListingCard layout. A highlight ring scans across
 * the cards and lands on the best deal, whose green chip pulses -
 * mirroring browse-then-spot-a-deal.
 *
 * Cards are always visible (no fade in/out) so the tile never looks empty
 * mid-cycle; the motion is the scanning highlight + chip pulse.
 * Transform/opacity only (no filter) so frames stay on the compositor.
 * memo()-wrapped to avoid re-rendering during resize.
 */
function ListingsAnimation() {
  const sceneRef = useScenePause<SVGSVGElement>();
  const W = 200;
  const H = 160;
  const CW = 54;   // card width
  const CH = 104;  // card height
  const TOP = 28;  // card top (vertically centred in the 160 box)
  const STEP = 64; // card-to-card x stride
  const X0 = 9;    // first card x

  // Card 0 (left) is the "deal" the scan settles on (emerald, pulsing
  // chip); middle is "fair" (amber); right is another deal.
  const cards = [
    { chip: "#16a34a", chipCls: "l-chip-1" },
    { chip: "#f59e0b", chipCls: "" },
    { chip: "#16a34a", chipCls: "" },
  ];

  return (
    <svg ref={sceneRef} viewBox={`0 0 ${W} ${H}`} className="scene-svg w-full h-44" aria-hidden="true">
      <defs>
        <linearGradient id="lcardPhoto" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#dbeafe" />
          <stop offset="1" stopColor="#c7d2fe" />
        </linearGradient>
      </defs>

      {cards.map((card, i) => {
        const x = X0 + i * STEP;
        const px = x + 5;       // photo x
        const py = TOP + 5;     // photo y
        const pw = CW - 10;     // photo w (44)
        const ph = 52;          // photo h
        const pb = py + ph;     // photo bottom
        return (
          <g key={i}>
            {/* card surface */}
            <rect x={x} y={TOP} width={CW} height={CH} rx="8" fill="var(--color-paper)" stroke="var(--color-ink)" strokeOpacity="0.1" />
            {/* photo + a tiny skyline so it reads as a real listing image */}
            <rect x={px} y={py} width={pw} height={ph} rx="5" fill="url(#lcardPhoto)" />
            <g fill="#93a5c0" opacity="0.9">
              <rect x={px + 8} y={pb - 16} width="7" height="16" rx="1" />
              <rect x={px + 17} y={pb - 23} width="8" height="23" rx="1" />
              <rect x={px + 27} y={pb - 13} width="7" height="13" rx="1" />
            </g>
            {/* verdict chip on the photo */}
            <g className={card.chipCls} style={{ transformBox: "fill-box", transformOrigin: "center" }}>
              <rect x={px + 3} y={py + 3} width="28" height="12" rx="6" fill={card.chip} fillOpacity="0.92" />
              <circle cx={px + 9} cy={py + 9} r="2.2" fill="#ffffff" />
              <rect x={px + 14} y={py + 6.5} width="12" height="5" rx="2.5" fill="#ffffff" fillOpacity="0.95" />
            </g>
            {/* price (bold) + location + ppm bars */}
            <rect x={x + 6} y={TOP + 64} width="34" height="6" rx="3" fill="var(--color-ink)" fillOpacity="0.6" />
            <rect x={x + 6} y={TOP + 76} width="42" height="5" rx="2.5" fill="var(--color-ink)" fillOpacity="0.22" />
            <rect x={x + 6} y={TOP + 87} width="26" height="5" rx="2.5" fill="var(--color-ink)" fillOpacity="0.22" />
            {/* LIVE beacon - the story is "these are genuinely on the
                market right now" (delisted ads drop out of the index),
                so every card carries a softly pulsing green status dot
                next to its price - liveness, not paperwork. */}
            <g className="l-live" style={{ transformBox: "fill-box", transformOrigin: "center", animationDelay: `${i * 0.5}s` }}>
              <circle cx={x + 46} cy={TOP + 67} r="3" fill="#16a34a" />
            </g>
            <circle cx={x + 46} cy={TOP + 67} r="3" fill="none" stroke="#16a34a" strokeWidth="1.4"
              className="l-live-ring" style={{ transformBox: "fill-box", transformOrigin: "center", animationDelay: `${i * 0.5}s` } as React.CSSProperties} />
            {/* Price-drop tag on the deal card - pops in as the scan
                settles: a white pill with a down-arrow + struck old
                price bar. The 15-minute tracking's story in 18x10px. */}
            {i === 0 && (
              <g className="l-drop" style={{ transformBox: "fill-box", transformOrigin: "center" }}>
                <rect x={px + pw - 21} y={pb - 13} width="19" height="10" rx="5" fill="#ffffff" fillOpacity="0.96" />
                <path d={`M ${px + pw - 16} ${pb - 10} l 2.6 4 l 2.6 -4 z`} fill="#16a34a" />
                <rect x={px + pw - 9.5} y={pb - 8.8} width="6" height="1.6" rx="0.8" fill="var(--color-ink)" fillOpacity="0.55" />
                <rect x={px + pw - 10.5} y={pb - 8.2} width="8" height="0.9" rx="0.45" fill="#e11d48" fillOpacity="0.85" transform={`rotate(-8 ${px + pw - 6.5} ${pb - 8})`} />
              </g>
            )}
          </g>
        );
      })}

      {/* Highlight ring - scans across the cards (base = card 0, translateX
          to the others) and settles on the left deal card. */}
      <g className="l-highlight">
        <rect x={X0} y={TOP} width={CW} height={CH} rx="8" fill="none" stroke="#6366f1" strokeWidth="2" />
      </g>

      <style>{`
        /* 5s loop: the highlight fades in on the right card, scans left
           through the middle, settles on the left (deal) card while its
           chip pulses, then fades at the seam so the restart is invisible. */
        @keyframes lHighlight {
          0%        { transform: translateX(128px); opacity: 0; }
          6%, 24%   { transform: translateX(128px); opacity: 1; }
          34%, 52%  { transform: translateX(64px);  opacity: 1; }
          62%, 88%  { transform: translateX(0px);   opacity: 1; }
          96%, 100% { transform: translateX(0px);   opacity: 0; }
        }
        @keyframes lChip1 {
          0%, 64%   { transform: scale(1); }
          72%, 82%  { transform: scale(1.16); }
          90%, 100% { transform: scale(1); }
        }
        /* Drop tag rides the same settle beat as the chip pulse: hidden
           during the scan, pops with it, holds, fades at the seam. */
        @keyframes lDrop {
          0%, 60%   { transform: scale(0.6); opacity: 0; }
          66%, 90%  { transform: scale(1);   opacity: 1; }
          97%, 100% { transform: scale(1);   opacity: 0; }
        }
        /* Liveness beacons: a slow breath on the dot + an expanding,
           fading ring - the universal "online now" grammar. Staggered
           per card via inline animation-delay. */
        @keyframes lLiveDot {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.25); }
        }
        @keyframes lLiveRing {
          0%   { transform: scale(1);   opacity: 0.9; }
          70%  { transform: scale(2.4); opacity: 0; }
          100% { transform: scale(2.4); opacity: 0; }
        }
        .l-highlight  { animation: lHighlight 5s cubic-bezier(0.4,0,0.2,1) infinite; }
        .l-chip-1     { animation: lChip1 5s ease-in-out infinite; }
        .l-drop       { animation: lDrop 5s ease-out infinite; }
        .l-live       { animation: lLiveDot 2.5s ease-in-out infinite; }
        .l-live-ring  { animation: lLiveRing 2.5s ease-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .l-highlight, .l-chip-1, .l-drop, .l-live, .l-live-ring { animation: none; }
          .l-highlight { opacity: 1; transform: translateX(0); }
          .l-drop { opacity: 1; transform: scale(1); }
          .l-live-ring { opacity: 0; }
        }
      `}</style>
    </svg>
  );
}

export default memo(ListingsAnimation);
